import {PartialRenderController} from 'extensions/ui-controllers/partial-render';
import {defineCustomElement} from 'extensions/utils';
import {classMap} from 'lit/directives/class-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import stylesheet from './x-mini-basket-group.component.scss?as=stylesheet';
import {html, LitElement, nothing} from 'lit';
import GTM from 'extensions/gtm';

export class MiniBasketGroup extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	// eslint-disable-next-line max-lines-per-function
	static get properties () {
		return {
			baseSubtotal: {
				attribute: 'base-subtotal',
				type: Number
			},
			brand: {
				stype: String
			},
			code: {
				type: String
			},
			formattedBaseSubtotal: {
				attribute: 'formatted-base-subtotal',
				type: String
			},
			formattedSubtotal: {
				attribute: 'formatted-subtotal',
				type: String
			},
			groupId: {
				attribute: 'group-id',
				type: Number
			},
			image: {
				type: String
			},
			link: {
				type: String
			},
			name: {
				type: String
			},
			options: {
				type: Array
			},
			optionsCount: {
				attribute: 'options-count',
				type: Number
			},
			price: {
				type: Number
			},
			quantity: {
				type: Number
			},
			subtotal: {
				type: Number
			},
			upsold: {
				type: Boolean
			}
		};
	}

	constructor () {
		super();

		this.gtm = new GTM(window.mivaJS);
		this.partialRenderController = new PartialRenderController(this);
		this.minibasket = this.parentElement;
		this.gtmList = 'Mini Basket';
		this.gtmListId = 'mini-basket';

		this.baseSubtotal = 0.00;
		this.brand = '';
		this.code = '';
		this.formattedBaseSubtotal = '';
		this.formattedSubtotal = '';
		this.groupId = 0;
		this.image = '';
		this.link = '';
		this.name = '';
		this.options = [];
		this.optionsCount = 0;
		this.price = 0.00;
		this.quantity = 0;
		this.subtotal = 0.00;
		this.upsold = false;
	}

	render () {
		const classes = {
			group: true,
			'group--has-options': this.optionsCount
		};

		return html`
			<div class="${classMap(classes)}">
				<a href="${this.link}" @click="${this.handleProductClick}" class="group-image">
					<img src="${this.image}" alt="${this.name} Thumbnail" class="group-img" loading="lazy" />
				</a>
				<div class="group-details">
					<div class="group-row group-row--top">
						<div class="group-details__large">
							<a href="${this.link}" @click="${this.handleProductClick}" class="group-name">
								${this.name}
							</a>
						</div>
						<div class="group-details__small">
							<div class="group-pricing">
								${this.renderPrice()}
							</div>
						</div>
					</div>
					${this.renderGroupOptions()}
					<div class="group-row group-row--bottom">
						<div class="group-details__large group-quantity">
							Qty: ${this.quantity}
						</div>
						${this.renderRemove()}
					</div>
				</div>
			</div>
		`;
	}

	renderRemove () {
		if (window.mivaJS.Screen === 'OCST' || window.mivaJS.Screen === 'OSEL' || window.mivaJS.Screen == 'OPAY') {
			return nothing;
		}

		return html`
			<div class="group-details__small group-remove">
				<button @click="${this.handleRemove}" class="group-remove-trigger">Remove</button>
			</div>
		`;
	}

	renderGroupOptions () {
		if (this.optionsCount === 0) {
			return nothing;
		}

		const options = [];

		for (const option of this.options) {
			options.push(this.renderGroupOption(option));
		}

		return html`
			<div class="group-options group-row--middle">
				${options}
			</div>
		`;
	}

	renderGroupOption (option) {
		let displayValue = option.opt_prompt?.toString();
		if (option.data) {
			displayValue = option.data.toString();
		} else if (option.data_long) {
			displayValue = option.data_long.toString();
		} else if (!option.option_id) {
			displayValue = '';
		}

		return html`
			<div class="group-option">
				<span class="group-option-prompt">${option.attr_prompt}${displayValue ? ':' : nothing}</span>
				${displayValue ? html`<span class="group-option-value">${unsafeHTML(displayValue)}</span>` : nothing}
			</div>
		`;
	}

	renderPrice () {
		let pricing = [];
		if (this.baseSubtotal > this.subtotal) {
			pricing.push(html`<s class="group-base-price">${this.formattedBaseSubtotal}</s>`);
		}

		pricing.push(html`<span class="group-price">${this.formattedSubtotal}</span>`);

		return html`${pricing}`;
	}

	handleProductClick () {
		this.handleActionEvent('Product Click');

		this.gtm.sendProductClick({
			brand: this.brand,
			id: this.code,
			/* eslint-disable camelcase */
			item_brand: this.brand,
			item_id: this.code,
			item_list_id: this.gtmListId,
			item_list_name: this.gtmList,
			item_name: this.name,
			/* eslint-enable camelcase */
			name: this.name,
			price: this.price
		});
	}

	async handleRemove () {
		const url = new URL(window.mivaJS.Merchant_Url);
		url.searchParams.set('Screen', 'BASK');
		url.searchParams.set('Action', 'RGRP');
		url.searchParams.set('Basket_Group', this.groupId);

		this.minibasket.loading = true;
		this.removeEvent();

		const response = await this.partialRenderController.send(url.toString(), ['global_minibasket']);
		const parser = new DOMParser();
		const responseDocument = parser.parseFromString(response, 'text/html');
		let element = responseDocument.querySelector('x-mini-basket');

		if (!element) {
			this.minibasket.loading = false;
			return;
		}

		if (window.mivaJS.Screen === 'BASK') {
			window.location.reload();
			return;
		}

		const miniBasketCount = element.getAttribute('count');
		this.minibasket.setAttribute('formatted-subtotal', element.getAttribute('formatted-subtotal'));
		this.minibasket.setAttribute('count', miniBasketCount);
		this.minibasket.innerHTML = element.innerHTML;

		const miniBasketCountElements = document.getElementsByClassName('js-mini-basket-count');
		for (const miniBasketCountElement of miniBasketCountElements) {
			miniBasketCountElement.innerText = miniBasketCount;
		}

		this.minibasket.loading = false;
	}

	removeEvent () {
		window.dataLayer = window.dataLayer || [];

		window.dataLayer.push({
			ecommerce: null
		});

		window.dataLayer.push({
			ecommerce: {
				remove: {
					products: [
						{
							index: 0,
							/* eslint-disable camelcase */
							item_brand: this.brand,
							item_id: this.code,
							item_list_id: this.gtmListId,
							item_list_name: this.gtmList,
							item_name: this.name,
							/* eslint-enable camelcase */
							price: this.price,
							quantity: this.quantity
						}
					]
				}
			},
			event: 'remove_from_cart'
		});

		this.handleActionEvent('Product Remove');
	}

	handleActionEvent (label) {
		this.gtm.sendEvent({
			action: 'Click',
			category: 'Mini Basket',
			label: label,
			noninteractionHit: false
		});
	}
};

defineCustomElement('x-mini-basket-group', MiniBasketGroup);
